import React, { useState, useMemo, useEffect } from 'react';

// This is the sequential grid setup.
// Left column: Clickable driver pool
// Right column: Grid slots that get filled one by one

// Map constructor names to their Tailwind CSS color *hex codes*
// This bypasses the Tailwind JIT/Purge issue.
const constructorToColorMap = {
  'Red Bull': '#8D8F93',
  'Ferrari': '#D90516',
  'Mercedes': '#00A19B',
  'McLaren': '#FF8000',
  'Aston Martin': '#037A68',
  'Alpine F1 Team': '#2173B8',
  'Williams': '#00A0DE',
  'RB F1 Team': '#003E60',
  'Sauber': '#00E676',
  'Haas F1 Team': '#E6002B',
  'AlphaTauri': '#1B2838',
  'Alfa Romeo': '#241F21',
  'Renault': '#FFD800',
  'Racing Point': '#EC0374',
  'Force India': '#737378',
  'Toro Rosso': '#002DFF',
  'Default': '#E5E5E6',
};

const constructorToLogoMap = {
  'Red Bull': '/RedBull.png',
  'Ferrari': '/Ferrari.png',
  'Mercedes': '/mercedes.png',
  'McLaren': '/mclaren.png',
  'Aston Martin': '/Aston.png',
  'Alpine F1 Team': '/Alpine.png',
  'Williams': 'Williams.png',
  'RB F1 Team': '/RBF1.png',
  'Sauber': '/Sauber.png',
  'Haas F1 Team': '/Haas.png',
  'AlphaTauri': '/AlphaTauri.png',
  'Alfa Romeo': '/AlphaRomeo.png',
  'Renault': '/Renault.png',
  'Racing Point': '/RacingPoint.png',
  'Force India': '/ForceIndia.png',    // Pre-Racing Point (Pink)
  'Toro Rosso': '#002DFF',      // Pre-AlphaTauri (Blue)
};

// Function to get the hex code, with a fallback
const getTeamColor = (constructorName) => {
  return constructorToColorMap[constructorName] || constructorToColorMap['Default'];
};
const getTeamLogo = (constructorName) => {
  return constructorToLogoMap[constructorName] || null;
};

// --- Left Column Component ---
const DriverPool = ({ drivers, onDriverSelect, assignedDrivers }) => {
  return (
    <div>
      <h3 className="text-xl text-white font-bold mb-4">Driver Pool</h3>
      {/* Re-using 'pr-2' from our previous step for the scrollbar */}
      <div className="flex flex-col space-y-2 max-h-[600px] overflow-y-auto pr-2">
        {drivers.sort((a, b) => {
          if (a.constructor_name < b.constructor_name) return -1;
          if (a.constructor_name > b.constructor_name) return 1;
          if (a.driver_name < b.driver_name) return -1;
          if (a.driver_name > b.driver_name) return 1;
          return 0;
        }).map((driver) => {
          const isAssigned = assignedDrivers.has(driver.driver_name);
          const color = getTeamColor(driver.constructor_name);
          const logo = getTeamLogo(driver.constructor_name);
          
          // Style only handles the solid background color
          const style = !isAssigned ? { backgroundColor: color } : {};

          return (
            <button
              key={driver.driver_name}
              onClick={() => onDriverSelect(driver.driver_name)}
              disabled={isAssigned}
              style={style}
              // --- CHANGE 1: Added 'relative' to act as a container
              // and 'overflow-hidden' to clip the logo if it's too big
              className={`relative overflow-hidden p-12 rounded-2xl text-left font-semibold transition-all duration-200
                          ${isAssigned 
                            ? 'bg-f1-gray text-gray-500 opacity-60 line-through' 
                            : 'text-white transform hover:brightness-125'
                          }
                        `}
            >
              {/* --- CHANGE 2: Added the logo as an <img> tag --- */}
              {/* It's positioned absolutely, bottom-right, with 30% opacity */}
              {logo && !isAssigned && (
                <img
                  src={logo}
                  alt="" // Decorative, so alt text is empty
                  className="absolute bottom-1 left-1 h-20 object-contain"
                />
              )}

              {/* --- CHANGE 3: Wrapped text in a relative span --- */}
              {/* This ensures the text always appears "on top" of the logo */}
              <span className="absolute backdrop-blur-sm p-1 top-1/2 left-[60%] -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col gap-1">
                <span className='text-lg'></span>{driver.driver_name}
                <span className="text-sm opacity-80">{driver.constructor_name}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- Right Column Component ---
const GridSlots = ({ grid, driverMap, activeSlotIndex, onSlotClick }) => {
  return (
    <div>
      <h3 className="text-xl text-white font-bold mb-4">Starting Grid</h3>
      {/* Re-using 'pr-2' for the scrollbar */}
      <div className="flex flex-col space-y-2 max-h-[600px] overflow-y-auto p-2">
        {grid.map((driverName, index) => {
          const position = index + 1;
          const isActive = (index === activeSlotIndex);
          
          let content;
          let bgColor = getTeamColor('Default'); // Start with default gray
          let textColor = 'text-gray-400'; // Default text for empty slot
          let constructorName = null;
          let logo = null; // 1. Initialize logo

          if (driverName) {
            constructorName = driverMap.get(driverName);
            bgColor = getTeamColor(constructorName); // Get team color
            textColor = 'text-white';
            logo = getTeamLogo(constructorName); // 2. Get the logo URL
            content = (
              <span className="font-bold">{driverName}</span>
            );
          } else {
            content = (
              <span className="opacity-70">P{position} - Select driver</span>
            );
          }

          return (
            <button
              key={position}
              onClick={() => onSlotClick(index)}
              style={{ backgroundColor: bgColor }}
              // 3. Add 'relative' and 'overflow-hidden'
              className={`relative overflow-hidden p-12 rounded-md text-left transition-all duration-200 w-full ${textColor}
                         ${isActive ? 'ring-4 ring-f1-red shadow-lg' : ''}
                         ${driverName ? 'cursor-pointer' : 'cursor-default'}
                        `}
              disabled={!driverName && !isActive} 
            >
              {/* 4. Add the logo image, identical to the DriverPool one */}
              {logo && (
                <img
                  src={logo}
                  alt="" // Decorative
                  className="absolute bottom-1 left-1 h-20 object-contain"
                />
              )}

              {/* 5. Wrap text content in a z-10 span to layer it on top */}
              <span className={`absolute top-1/2 -translate-y-1/2 ${driverName ? 'left-[60%] -translate-x-1/2' : ''} z-10`} >
                <span className="w-8 inline-block font-bold opacity-80">{position}</span>
                {content}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- Main Component ---
function GridSetup({ raceInfo, onGridComplete, onBack }) {
  const { drivers } = raceInfo;

  // State: An array of 20 elements (driver names or null)
  const [grid, setGrid] = useState(Array(drivers.length).fill(null));
  
  // State: The index of the *next slot to be filled* (0-19)
  const [activeSlotIndex, setActiveSlotIndex] = useState(0);

  // A memoized Map for quick lookups
  const { driverMap, assignedDrivers } = useMemo(() => {
    const dMap = new Map(drivers.map(d => [d.driver_name, d.constructor_name]));
    const aDrivers = new Set(grid.filter(d => d !== null));
    return { driverMap: dMap, assignedDrivers: aDrivers };
  }, [drivers, grid]);

  // Find the next empty slot to highlight
  useEffect(() => {
    const nextEmptyIndex = grid.findIndex(driver => driver === null);
    setActiveSlotIndex(nextEmptyIndex === -1 ? null : nextEmptyIndex);
  }, [grid]);

  // --- Interaction Handlers ---

  // 1. User clicks a driver from the pool (Left Column)
  const handleDriverSelect = (driverName) => {
    // If no slot is active (grid is full) or driver is already assigned, do nothing
    if (activeSlotIndex === null || assignedDrivers.has(driverName)) return;

    const newGrid = [...grid];
    newGrid[activeSlotIndex] = driverName;
    setGrid(newGrid);
  };

  // 2. User clicks a grid slot (Right Column)
  const handleSlotClick = (slotIndex) => {
    const driverInSlot = grid[slotIndex];

    // If the slot is *filled*, un-assign it and make it active
    if (driverInSlot) {
      const newGrid = [...grid];
      newGrid[slotIndex] = null; // Empty the slot
      setGrid(newGrid);
      setActiveSlotIndex(slotIndex); // Make this the new active slot
    }
    // If the slot is empty, just make it active
    else {
      setActiveSlotIndex(slotIndex);
    }
  };

  // --- Validation and Submission ---
  const isGridComplete = activeSlotIndex === null;

  const handleSubmit = () => {
    const finalGridObject = {};
    grid.forEach((driverName, index) => {
      finalGridObject[driverName] = index + 1;
    });
    onGridComplete(finalGridObject);
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-f1-black">Set The Grid</h2>
          <p className="text-lg text-f1-red">{raceInfo.raceName} ({raceInfo.year})</p>
        </div>
        <button
          onClick={onBack}
          className="py-2 px-4 border border-f1-gray-dark text-f1-black font-semibold rounded-md hover:text-white hover:bg-f1-red transition-colors"
        >
          &larr; Back
        </button>
      </div>
      
      <p className="text-f1-gray-dark text-lg mb-6 text-center md:text-left">
        {isGridComplete 
          ? "The grid is set!"
          : <>Click a driver from the pool to assign them to the <span className="text-f1-red font-bold">highlighted</span> slot.</>
        }
      </p>

      {/* Main Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column */}
        <DriverPool 
          drivers={drivers}
          onDriverSelect={handleDriverSelect}
          assignedDrivers={assignedDrivers}
        />
        
        {/* Right Column */}
        <GridSlots
          grid={grid}
          driverMap={driverMap}
          activeSlotIndex={activeSlotIndex}
          onSlotClick={handleSlotClick}
        />
      </div>

      {/* Footer / Submit Button */}
      <div className="mt-8 text-center">
        {!isGridComplete && (
          <p className="text-yellow-400 mb-4">
            {drivers.length - assignedDrivers.size} drivers remaining.
          </p>
        )}
        <button
          onClick={handleSubmit}
          disabled={!isGridComplete}
          className="w-full md:w-1/2 py-4 px-8 bg-f1-red text-white text-xl font-bold rounded-lg shadow-lg transition-all duration-300
                     disabled:bg-f1-gray disabled:opacity-50 disabled:cursor-not-allowed
                     hover:bg-red-700"
        >
          LIGHTS OUT!
        </button>
      </div>
    </div>
  );
}

export default GridSetup;